import { useEffect, useRef, useState, type ReactNode } from 'react';
import { QUICK_REACTIONS, reactionPickerDelay } from '../../../../lib/message-reactions';
import { useTranslation } from '../../../../lib/i18n';
import { IconButton } from '../../ui';
import styles from './ReactionPicker.module.css';

// The tool bar's emoji picker (Figma 22838:305981, the "Chat Panel Reactions" prototype): a glass
// capsule that springs up OVER the message from the React button it is anchored to, its emoji rising
// in one after another. It floats rather than opening a row below, so opening it never pushes the
// transcript. It offers the quick six, and hugs them.
//
// Same geometry, set and timing as iOS `AssistantReactionPicker` and Android's picker in `AssistantToolbar.kt`.
// Outside press and Escape close it; the parent owns `open`.
export function ReactionPickerAnchor({
  open,
  onPick,
  onClose,
  children,
}: {
  open: boolean;
  onPick: (emoji: string) => void;
  onClose: () => void;
  // The React toggle the capsule is anchored to.
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLSpanElement>(null);
  // Kept mounted for the close fade, then dropped — a closed row carries no picker DOM.
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    const id = setTimeout(() => setMounted(false), PICKER_CLOSE_MS);
    return () => clearTimeout(id);
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!anchorRef.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  return (
    <span ref={anchorRef} className={styles.anchor}>
      {children}
      {mounted && (
        <div role="group" aria-label={t('chat.reactions.react')} className={open ? styles.capsule : `${styles.capsule} ${styles.closing}`}>
          <div className={styles.row}>
            {QUICK_REACTIONS.map((emoji, i) => (
              <IconButton
                key={emoji}
                size="medium"
                variant="text"
                className={styles.emoji}
                style={{ animationDelay: `${reactionPickerDelay(i)}ms` }}
                onClick={() => onPick(emoji)}
                aria-label={emoji}
                title={emoji}
              >
                <span aria-hidden className={styles.glyph}>
                  {emoji}
                </span>
              </IconButton>
            ))}
          </div>
        </div>
      )}
    </span>
  );
}

/** The React toggle's held look while its picker is open (the prototype's pressed fill + 1.08 lift). */
export const reactToggleOpenClass = styles.toggleOpen;

// How long the capsule takes to fade out once closed — matches `.closing` in the stylesheet.
const PICKER_CLOSE_MS = 200;
